import { CommonModule } from '@angular/common';
import {
  Component,
  OnDestroy,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { Subject, timer } from 'rxjs';
import { switchMap, takeUntil, takeWhile, tap } from 'rxjs/operators';
import { CheckoutService } from '../../services/checkout.service';
import { PAYMENTS_CONFIG } from '../../payments.config';
import { EStatus } from '../../models/payment.enums';
import { PaymentConfig, PaymentResult } from '../../models/payment.models';
import { NumericOnlyDirective } from '../../directives/numeric-only.directive';

/**
 * EcoCash mobile-money payments. The phone number is captured with an
 * ng-zorro form; once initiated the transaction status is polled and shown
 * inside an html-driven `nz-modal`.
 */
@Component({
  selector: 'lib-ecocash',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NzFormModule,
    NzInputModule,
    NzButtonModule,
    NzModalModule,
    NzSpinModule,
    NumericOnlyDirective,
  ],
  templateUrl: './ecocash.html',
  styleUrl: './ecocash.css',
})
export class EcocashComponent implements OnDestroy {
  readonly payloadConfig = input.required<PaymentConfig>();
  /**
   * Compact, labelled layout used inside the checkout dialog — the phone field
   * sits in a tinted panel and the terms/fee notes are dropped.
   */
  readonly compact = input(false);
  readonly paymentSubmitted = output<PaymentResult>();

  private readonly fb = inject(FormBuilder);
  private readonly checkoutService = inject(CheckoutService);
  private readonly config = inject(PAYMENTS_CONFIG);
  private readonly message = inject(NzMessageService);

  private readonly destroy$ = new Subject<void>();

  readonly EcocashBin = '910012';
  readonly minAmount = this.config.production ? 1 : 0.1;
  readonly EStatus = EStatus;

  readonly isPaymentInitiated = signal(false);
  readonly isLoading = signal(false);
  readonly checkoutInfo = signal<any>(null);
  readonly copied = signal(false);
  user: any;

  paymentForm: FormGroup;

  constructor() {
    const rawUser = localStorage.getItem(this.config.userStorageKey ?? 'auth_user');
    this.user = rawUser ? JSON.parse(rawUser) : null;

    this.paymentForm = this.fb.group({
      phoneNumber: ['', [Validators.required, Validators.pattern('^[0-9]{9,10}$')]],
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Virtual PAN EcoCash expects: bin + phone. */
  get pan(): string {
    const phone = this.paymentForm.get('phoneNumber')?.value ?? '';
    return this.EcocashBin + phone;
  }

  /** Expiry as MMYY — current month, next year (e.g. 0427). */
  get ecocashExpiryDate(): string {
    const now = new Date();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const year = String(now.getFullYear() + 1).slice(-2);
    return `${month}${year}`;
  }

  generateReferenceAndSubmit(feeId: string | null = null): void {
    if (!this.user) {
      this.message.error('User session not found. Please log in again.');
      return;
    }

    const billedAmount = this.payloadConfig()?.Amount ?? 0;
    if (!billedAmount || billedAmount < this.minAmount) {
      this.message.error(`Amount should be at least ${this.minAmount}`);
      return;
    }

    const payload: any = {
      userId: this.user.id,
      feeId,
      amount: billedAmount,
      callbackUrl: this.payloadConfig().isWallet
        ? this.checkoutService.frontendUrl + 'wallet'
        : this.checkoutService.frontendUrl,
      paymentChannel: this.payloadConfig().paymentChannel,
      pan: this.pan,
      expiryDate: this.ecocashExpiryDate,
      cardSecurityCode: '123',
    };

    this.isLoading.set(true);

    this.checkoutService
      .generateMerchantReferenceWithoutFee(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.isPaymentInitiated.set(true);
          this.startPolling(res.invoiceNumber);
        },
        error: () => {
          this.isLoading.set(false);
          this.message.error('Failed to generate payment reference. Please try again.');
        },
      });
  }

  private startPolling(reference: string): void {
    timer(0, 3000)
      .pipe(
        switchMap(() => this.checkoutService.getReference(reference)),
        tap((res) => {
          this.checkoutInfo.set(res);
          this.isLoading.set(false);
        }),
        takeWhile((res) => res.transactionStatus === EStatus.PENDING, true),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (res) => {
          if (res.transactionStatus === EStatus.COMPLETED) {
            this.paymentSubmitted.emit({ success: true, data: res });
          }
        },
        error: () => {
          this.isLoading.set(false);
          this.message.error('Could not retrieve payment status. Please check your payment history.');
        },
      });
  }

  closeStatus(): void {
    this.isPaymentInitiated.set(false);
    this.checkoutInfo.set(null);
  }

  copyToClipboard(): void {
    navigator.clipboard.writeText(this.checkoutInfo()?.invoiceNumber ?? '').then(() => {
      this.copied.set(true);
      setTimeout(() => this.copied.set(false), 3000);
    });
  }
}
